<template>
    <div v-if="can.update.conference_room">
        <button-secondary @click="open()">
            <template slot="icon"><i class="fa fa-pencil"></i></template>
            Konferenzraum bearbeiten
        </button-secondary>

        <portal to="modal" v-if="showModal">
            <modal @close="showModal = false">
                <template slot="header">Seminarraum bearbeiten</template>

                <div class="flex justify-end">
                    <confirm-dialog @confirmed="remove">
                        <button-delete>
                            <template slot="icon"><i class="fa fa-trash"></i></template>
                            Konferenzraum löschen
                        </button-delete>
                        <template slot="question">Soll der Konferenzraum <span class="font-bold">{{ item.name }}</span>
                            wirklich gelöscht werden?
                        </template>
                        <template slot="header">Konferenzraum löschen</template>
                        <template slot="confirm">
                            <button-delete>
                                <template slot="icon"><i class="fa fa-trash"></i></template>
                                Konferenzraum löschen
                            </button-delete>
                        </template>
                    </confirm-dialog>
                </div>

                <input-text v-model="name" class="mb-4">
                    Name des Konferenzraums
                </input-text>

                <input-text v-model="details" class="mb-8">
                    Details
                    <template v-slot:helper>
                        z.B. Tel. Nummer, Ohne Fenster, ...
                    </template>
                </input-text>

                <input-text v-model="persons" class="mb-4" type="number">
                    Maximale Anzahl an Personen
                </input-text>

                <select-menu
                    class="mb-8"
                    v-model="floor"
                    :values="[null,...floors.map(g => g.id)]"
                    :text="['Ohne Etage',...floors.map(g => g.name)]">
                    Etage
                </select-menu>

                <multi-select class="mb-4" v-model="selectedEquipment" :selectable="equipment"
                              v-show="equipment.length > 0">
                    Ausstattung
                </multi-select>

                <multi-select class="mb-4" v-model="selectedPostableEquipment" :selectable="postableEquipment"
                              v-show="postableEquipment.length > 0">
                    Zubuchbare Ausstattung
                </multi-select>

                <multi-select class="mb-4" v-model="selectedRoomLayouts" :selectable="roomLayouts"
                              v-show="roomLayouts.length > 0">
                    Auswählbare Raumlayouts
                </multi-select>

                <selection
                    v-model="defaultRoomLayout"
                    :values="roomLayoutNames"
                    placeholder="Keine"
                    class="mb-4"
                >
                    Standard Raumlayout
                </selection>

                <multi-select class="mb-4" v-model="selectedCaterings" :selectable="caterings"
                              v-show="caterings.length > 0">
                    Catering Optionen
                </multi-select>

                <toggle-group v-model="requiresConfirmation">
                    Buchungen benötigen eine Bestätigung vom Admin
                    <template v-slot:helper>Hinweis: kann überschrieben werden durch spezielle Benutzergruppen</template>
                    <template v-slot:body>
                        <input-text type="text" v-model="notificationEmail">
                            Benachrichtigungs-E-Mail
                            <template v-slot:helper>Standard werden alle Admins in der App benachrichtigt, zusätzlich kann man hier E-Mail Adressen eintragen ( mit Komma getrennt ) welche dann die Benachrichtigung separat per E-Mail bekommen.</template>
                        </input-text>
                    </template>
                </toggle-group>

                <toggle-group v-model="isVirtual">
                    Der Raum ist ein virtueller Raum und beinhaltet folgende Unterräume
                    <template v-slot:helper>Hinweis: Diese Räume werden zum Zweck Kombi-Raum Buchung verwendet</template>
                    <template v-slot:body>
                        <div class="flex justify-around mt-2">
                            <select-menu
                                class="mb-8"
                                v-show="isVirtual"
                                v-model="associatedRooms.left"
                                :values="conferenceRooms.map(g => g.id)"
                                :text="conferenceRooms.map(g => g.text)">
                                Unterraum Links
                            </select-menu>

                            <select-menu
                                class="mb-8"
                                v-show="isVirtual"
                                v-model="associatedRooms.right"
                                :values="conferenceRooms.map(g => g.id)"
                                :text="conferenceRooms.map(g => g.text)">
                                Unterraum Rechts
                            </select-menu>
                        </div>
                    </template>
                </toggle-group>

                <div v-if="true" class="mb-4">
                    <conference-room-position-edit v-model="item"></conference-room-position-edit>
                </div>

                <div class="flex justify-end">
                    <button-primary @click="store">
                        <template slot="icon"><i class="fa fa-save"></i></template>
                        Speichern
                    </button-primary>
                </div>
            </modal>
        </portal>
    </div>
</template>

<script>
import ButtonSecondary from "./UI/ButtonSecondary";
import Modal from "./Modal";
import ButtonPrimary from "./UI/ButtonPrimary";
import InputText from "./UI/InputText";
import ButtonDelete from "./UI/ButtonDelete";
import ConfirmDialog from "./UI/ConfirmDialog";
import MultiSelect from "./UI/MultiSelect";
import ToggleGroup from "./UI/ToggleGroup.vue";
import SelectMenu from "./UI/SelectMenu.vue";
import ConferenceRoomPositionEdit from "./ConferenceRoom/ConferenceRoomPositionEdit.vue";
import Selection from "./UI/Selection.vue";

export default {
    name: "ConferenceRoomEdit",

    components: {
        Selection,
        ConferenceRoomPositionEdit,
        SelectMenu,
        ToggleGroup,
        ButtonDelete,
        ButtonPrimary,
        ButtonSecondary,
        ConfirmDialog,
        InputText,
        Modal,
        MultiSelect,
    },

    props: ['item', 'rooms'],

    inject: ['can'],

    data: (vm) => ({
        showModal: false,
        name: vm.item.name,
        persons: vm.item.persons,
        details: vm.item.details,
        requiresConfirmation: vm.item.requires_confirmation,
        equipment: [],
        selectedEquipment: [],

        postableEquipment: [],
        selectedPostableEquipment: [],

        roomLayouts: [],
        selectedRoomLayouts: [],

        caterings: [],
        selectedCaterings: [],
        floors: [],
        floor: vm.item.floor_id,
        isVirtual: vm.item.is_virtual,
        associatedRooms: {
            'left': null,
            'right': null,
        },
        defaultRoomLayout: null,
    }),
    methods: {
        async open() {
            this.showModal = true;

            const response = await axios.get('/admin/floors');
            this.floors = await response.data.data;

            const responseEquipment = await axios.get('/admin/equipment');
            this.equipment = await responseEquipment.data.data.map(a => ({
                id: a.id,
                text: a.name,
            }));
            this.selectedEquipment = this.item.equipment.map(a => a.id);

            const responsePostableEquipment = await axios.get('/admin/postable_equipment');
            this.postableEquipment = await responsePostableEquipment.data.data.map(a => ({
                id: a.id,
                text: a.name,
            }));
            this.selectedPostableEquipment = this.item.postable_equipment.map(a => a.id);

            const responseLayout = await axios.get('/admin/room_layouts');
            this.roomLayouts = await responseLayout.data.data.map(a => ({
                id: a.id,
                text: a.name,
            }));
            this.selectedRoomLayouts = this.item.room_layouts.map(a => a.id);
            this.defaultRoomLayout = this.item.room_layouts.filter(a => a.pivot.is_default).map(a => a.id)[0] ?? null;

            const responseCatering = await axios.get('/admin/caterings');
            this.caterings = await responseCatering.data.data.map(a => ({
                id: a.id,
                text: a.name,
            }));
            this.selectedCaterings = this.item.caterings.map(a => a.id);

            this.associatedRooms.left = this.item.associated_rooms.find(r => r.pivot.side === 'left')?.id;
            this.associatedRooms.right = this.item.associated_rooms.find(r => r.pivot.side === 'right')?.id;
        },
        store() {
            this.$http.post('/admin/conference_rooms/' + this.item.id, {
                _method: 'put',
                name: this.name,
                persons: this.persons,
                details: this.details,
                equipment: this.selectedEquipment,
                postable_equipment: this.selectedPostableEquipment,
                room_layouts: this.selectedRoomLayouts,
                default_room_layout: this.defaultRoomLayout,
                caterings: this.selectedCaterings,
                requires_confirmation: this.requiresConfirmation,
                email_notification: this.notificationEmail ? this.notificationEmail.replace(/\s/g,'').split(',') : [],
                floor_id: this.floor,
                position: this.item.position ?? null,
                is_virtual: this.isVirtual,
                associated_rooms: this.associatedRooms,
            }).then(() => {
                this.showModal = false;
                this.$emit('updated');
                this.$root.$emit('alert', 'Gespeichert.');
            }).catch(error => {
                this.$root.$emit('error', error);
            })
        },
        remove() {
            this.$http.post(`/admin/conference_rooms/${this.item.id}`, {
                _method: 'delete',
            }).then(() => {
                this.showModal = false;
                this.$emit('updated');
                this.$root.$emit('alert', 'Raum gelöscht.');
            }).catch(error => {
                this.$root.$emit('error', error);
            })
        },
    },

    computed: {
        notificationEmail: {
            get() {
                return this.item.email_notification.toString()
            },
            set(val) {
                this.item.email_notification = val
            }
        },
        roomLayoutNames: {
            get() {
                return this.roomLayouts
                    .filter((item) => this.selectedRoomLayouts.includes(item.id))
                    .reduce((v, item) => {
                        v[item.id] = item.text;
                        return v;
                    },
                    {})
            }
        },
        conferenceRooms: {
            get() {
                return this.rooms.filter(a => {
                    return a.id !== this.item.id && !a.is_virtual;
                })
                    .map(a => ({
                        id: a.id,
                        text: a.name,
                    }))
            }
        }
    }
}
</script>

<style scoped>

</style>